import axios from 'axios';
import { app } from "@microsoft/teams-js";
import { toast } from 'react-toastify';

export class ApiService {


    static isLocalEnv() {
        return window.location.hostname === "localhost";
    }

    // get the base url for the api
    private static async getBaseUrl(): Promise<string> {
        if (ApiService.isLocalEnv()) {
            return "https://localhost:56800";
        }



        const context = await app.getContext();
        const team = context.sharePointSite?.teamSitePath;

        const projectName = team?.replace("/sites/", "").replace("-02.Opvolgen", "").replace("-Opvolgen", "");

        let envName = projectName?.replace(/\s+/g, "").match(/DOSP-([a-z, A-Z,-]*)-\d+/);
        if (!envName) {
            toast.error("Er is iets misgegaan bij het laden van de gegevens. Neem contact op met je systeembeheerder.");
            return ""; // Return an empty string or handle the error as needed
        }

        return `https://dosp-${envName[1]}-api.azurewebsites.net`;
    }

    static async setApiToken(ssoToken: string) {
        if (!ssoToken || sessionStorage.getItem('token')) {
            return;
        }
        if (!sessionStorage.getItem('ssoToken')) {
            sessionStorage.setItem('ssoToken', ssoToken);
        }

        let url = await this.getBaseUrl() + '/api/AuthenticateAD';
        const authHeader = `Bearer ${ssoToken}`;

        const token = await axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });

        sessionStorage.setItem('token', token.data.token);
    }

    static async getProjectData(projectName: string) {
        let url = await this.getBaseUrl() + '/api/enduser/ProjectInformation/' + projectName;
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getContactData() {
        const url = await this.getBaseUrl() + '/api/enduser/customer/Contact';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getAccountsData() {
        const url = await this.getBaseUrl() + '/api/enduser/customer/Account/Metadata';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getFiles(dospId: string) {
        const url = await this.getBaseUrl() + '/api/metadata/Files/' + dospId + '/File';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async downloadFile(dospId: string, fileId: string) {
        const url = await this.getBaseUrl() + '/api/metadata/Files/' + dospId + '/File/' + fileId;
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async uploadFile(fileId: string, fileContent: string, fileName: string, mimeType: string) {

        var data = {

            "fileContent": fileContent,
            "fileName": fileName,
            "mimeType": mimeType
        }

        const url = await this.getBaseUrl() + '/api/metadata/Files/' + fileId + '/FileUpload';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    // META DATA
    static async getAccountTypes() {
        const url = await this.getBaseUrl() + '/api/metadata/AccountType';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }
    static async getAccountTypesOnly() {
        const url = await this.getBaseUrl() + '/api/metadata/AccountType/Type';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }
    static async getAccountSubTypesOnly() {
        const url = await this.getBaseUrl() + '/api/metadata/AccountType/SubType';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getMemberTypes() {
        const url = await this.getBaseUrl() + '/api/metadata/MemberType';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getRoleTypes() {
        const url = await this.getBaseUrl() + '/api/metadata/Type/memberroles';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getThemes() {
        const url = await this.getBaseUrl() + '/api/metadata/Theme';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getDisciplineCodes() {
        const url = await this.getBaseUrl() + '/api/metadata/DisciplineCode';
        const token = await getToken();

        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async getContactGroups() {
        const url = await this.getBaseUrl() + '/api/metadata/ContactGroup';
        const token = await getToken();


        const authHeader = ` ${token}`;
        return axios.get(url, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    // UPSERT
    static async upsertContact(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/ProjectMemberRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postThemes(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/ThemeRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postDisciplineCodes(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/DisciplineCodeRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postFocuslines(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/FocuslineRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postProject(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/RequestChangeOnGeneralInfo';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postStakeholder(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/ProjectMemberRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }

    static async postPartner(data: any) {
        const url = await this.getBaseUrl() + '/api/enduser/StakeholderRequest/NewPartnerRequest';
        const token = await getToken();
        const authHeader = ` ${token}`;
        return axios.post(url, data, {
            headers: {
                accept: 'application/json',
                Authorization: authHeader,
            },
        });
    }
}

async function getToken(): Promise<string | null> {
    let token = sessionStorage.getItem('token');
    if (!token) {
        await ApiService.setApiToken(sessionStorage.getItem('ssoToken') || '');
        token = sessionStorage.getItem('token');
    }
    return token;
}